/**
 * Tier names — every screen reads a tier's name from here, never from a literal.
 *
 * The names come from `GET /tiers/names`, where a superadmin edits them in
 * Admin → Tier Names. Both languages arrive together and the viewer's language
 * decides which is shown, the same way localized database content works.
 *
 * Usage:
 *   const { tierName, tierLabel, tierColor } = useTierNames();
 *   <span>{tierName(user.tier)}</span>
 *   <span>{tierName('tier1', 'gallery')}</span>   // reads as the gallery tier
 */

import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useLanguageStore } from '@/stores/languageStore';

export interface TierNameEntry {
  tier: string;
  name_en: string;
  name_de: string;
  label_en: string;
  label_de: string;
  color?: string;
}

/**
 * Mirrors canonicalTier() in api/src/modules/tiers/names.ts.
 *
 * Shared Tiers: a tier key is one paid level read as one role, so an account
 * paying for Art Venue Pro reads as Artist Pro on its artist profile. The level
 * decides; the role decides which name the level is shown under.
 */
const TIER_LEVELS: Record<string, 0 | 1 | 2> = {
  free: 0, tier1: 1, tier2: 2, pioneer: 2,
  gallery_free: 0, gallery_tier1: 1, gallery_tier2: 2,
  explorer_free: 0, explorer_active: 1, explorer_pro: 2,
};

const TIER_BY_ROLE: Record<string, [string, string, string]> = {
  artist: ['free', 'tier1', 'tier2'],
  gallery: ['gallery_free', 'gallery_tier1', 'gallery_tier2'],
  explorer: ['explorer_free', 'explorer_active', 'explorer_pro'],
};

/** The paid level a tier key carries. Unknown keys read as Free. */
export function tierLevel(tier?: string | null): 0 | 1 | 2 {
  return TIER_LEVELS[(tier || 'free').trim()] ?? 0;
}

function roleOf(profileType?: string): string {
  if (profileType === 'gallery' || profileType === 'museum') return 'gallery';
  if (profileType === 'explorer' || profileType === 'curator') return 'explorer';
  return 'artist';
}

export function canonicalTier(tier?: string | null, profileType?: string): string {
  const t = (tier || 'free').trim();
  if (t === 'pioneer') return 'pioneer';
  if (profileType) return TIER_BY_ROLE[roleOf(profileType)][tierLevel(t)];
  return t;
}

// One fetch per page load, shared by every component that asks.
let cache: TierNameEntry[] | null = null;
let inflight: Promise<TierNameEntry[]> | null = null;
const listeners = new Set<(t: TierNameEntry[]) => void>();

export async function loadTierNames(force = false): Promise<TierNameEntry[]> {
  if (cache && !force) return cache;
  if (!inflight || force) {
    inflight = api
      .get<{ tiers: TierNameEntry[] }>('/tiers/names')
      .then((d) => {
        cache = d.tiers || [];
        listeners.forEach((fn) => fn(cache!));
        return cache;
      })
      .catch(() => {
        cache = cache || [];
        return cache;
      })
      .finally(() => { inflight = null; });
  }
  return inflight;
}

export function useTierNames() {
  const language = useLanguageStore((s) => s.language);
  const [tiers, setTiers] = useState<TierNameEntry[]>(cache || []);

  useEffect(() => {
    let alive = true;
    const onChange = (next: TierNameEntry[]) => { if (alive) setTiers(next); };
    listeners.add(onChange);
    loadTierNames().then(onChange);
    return () => { alive = false; listeners.delete(onChange); };
  }, []);

  const entry = (tier?: string | null, profileType?: string) => {
    const key = canonicalTier(tier, profileType);
    return tiers.find((t) => t.tier === key);
  };
  const pick = (en?: string, de?: string) =>
    (language === 'de' ? (de || en) : (en || de)) || '';

  return {
    tiers,
    /** The tier's name in the viewer's language. Falls back to the raw key. */
    tierName: (tier?: string | null, profileType?: string): string => {
      const e = entry(tier, profileType);
      return e ? pick(e.name_en, e.name_de) : (tier || '');
    },
    /** The small caption under the name ('Early Access', …). May be empty. */
    tierLabel: (tier?: string | null, profileType?: string): string => {
      const e = entry(tier, profileType);
      return e ? pick(e.label_en, e.label_de) : '';
    },
    tierColor: (tier?: string | null, profileType?: string): string =>
      entry(tier, profileType)?.color || '#6B7280',
    /** Re-read after an edit so the change shows without a page reload. */
    reload: () => loadTierNames(true),
  };
}
